import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Plus } from "lucide-react";
import { uploadImageToCloudflare, uploadVideoToCloudflare } from "../../api/cloudflareUpload";
import { getInvalidUploadFilenameMessage, isUploadFilenameValid } from "../../utils/fileUploadValidation";

const ContentUploadPanel = ({
    initialPreview = "",
    initialFileName = "",
    readOnly = false,
    showUpload = true,
    autoUpload = true,
    showError = false,
    selectedFileType = "",
    uploadEnabled = true,
    helperText = "Video up to 100mb",
    onFileSelect,
    onUploadStart,
    onUploadComplete,
    onUploadError,
    onClear,
}) => {
    const { t } = useTranslation();
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(initialPreview);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        setSelectedFile(null);
        setPreviewUrl(initialPreview || "");
    }, [initialPreview]);

    useEffect(() => {
        if (!selectedFile) {
            return;
        }
        const objectUrl = URL.createObjectURL(selectedFile);
        setPreviewUrl(objectUrl);
        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [selectedFile]);

    useEffect(() => {
        if (!selectedFile) return;
        setSelectedFile(null);
        setPreviewUrl("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        onClear?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFileType]);

    const handleClear = () => {
        setSelectedFile(null);
        setPreviewUrl("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        onClear?.();
    };

    const handleUploadClick = () => {
        if (readOnly) return;
        if (!uploadEnabled) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0] || null;
        const expectedFileType = String(selectedFileType || "").toLowerCase();

        if (file && !isUploadFilenameValid(file.name)) {
            setSelectedFile(null);
            onFileSelect?.(null);
            onUploadError?.(getInvalidUploadFilenameMessage());
            event.target.value = "";
            return;
        }

        if (
            file &&
            expectedFileType &&
            ((expectedFileType === "video" && !file.type?.startsWith("video/")) ||
                (expectedFileType === "image" && !file.type?.startsWith("image/")))
        ) {
            setSelectedFile(null);
            onFileSelect?.(null);
            onUploadError?.(`${t("CONTENT_UPLOAD_ERROR_UPLOAD_A")} ${selectedFileType.toLowerCase()} ${t("CONTENT_UPLOAD_ERROR_FILE")}`);
            event.target.value = "";
            return;
        }

        setSelectedFile(file);
        onFileSelect?.(file);
        if (!file) return;
        if (!autoUpload) return;

        setIsUploading(true);
        setUploadProgress(0);
        onUploadStart?.();

        const uploader = file.type?.startsWith("video/")
            ? uploadVideoToCloudflare
            : uploadImageToCloudflare;

        uploader(file, setUploadProgress)
            .then((result) => {
                onUploadComplete?.({
                    ...result,
                    fileType: file.type?.startsWith("video/") ? "Video" : "Image",
                });
            })
            .catch((error) => {
                handleClear();
                onUploadError?.(error?.message || t("CONTENT_UPLOAD_ERROR_FAILED"));
            })
            .finally(() => {
                setIsUploading(false);
            });
    };

    const showVideoPreview =
        selectedFile?.type?.startsWith("video/") || String(previewUrl).includes("videodelivery.net");
    const hasPreview = Boolean(previewUrl);
    const canUpload = showUpload && !readOnly;
    const acceptedMime = selectedFileType === "Video"
        ? "video/*"
        : selectedFileType === "Image"
            ? "image/*"
            : "image/*,video/*";

    return (
        <>
            <div className={`relative flex flex-col overflow-hidden rounded-md border border-slate-200 bg-slate-50 text-center ${showVideoPreview && hasPreview ? "" : "h-full"} ${showError ? "border-rose-300" : ""}`}>
                {showError && (
                    <div className="mx-4 mt-4 rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white">
                        {t("CONTENT_UPLOAD_ERROR_FILE_SIZE")}
                    </div>
                )}
                {hasPreview ? (
                    <div className={`relative overflow-hidden ${showVideoPreview ? "aspect-video w-full" : "flex flex-1 items-center justify-center"}`}>
                        {canUpload && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="absolute right-3 top-3 z-10 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-lg hover:bg-slate-50 focus:outline-none"
                            >
                                <Trash2 className="h-6 w-6 text-rose-500" />
                            </button>
                        )}
                        {showVideoPreview ? (
                            String(previewUrl).includes("videodelivery.net") ? (
                                <iframe
                                    src={previewUrl}
                                    title={t("CONTENT_UPLOAD_VIDEO_PREVIEW")}
                                    className="h-full w-full border-0"
                                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                    allowFullScreen
                                />
                            ) : (
                                <video
                                    src={previewUrl}
                                    className="h-full w-full object-cover"
                                    controls
                                />
                            )
                        ) : (
                            <img
                                src={previewUrl}
                                alt=""
                                className="max-h-full max-w-full object-contain"
                            />
                        )}
                    </div>
                ) : null}

                {canUpload && !hasPreview ? (
                    <div
                        role="button"
                        tabIndex={uploadEnabled ? 0 : -1}
                        onClick={handleUploadClick}
                        onKeyDown={(e) => e.key === "Enter" && handleUploadClick()}
                        className={`flex flex-1 flex-col items-center justify-center gap-3 p-6 ${
                            uploadEnabled ? "cursor-pointer hover:bg-slate-100 focus:outline-none" : "cursor-not-allowed focus:outline-none"
                        }`}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={acceptedMime}
                            className="hidden"
                            onChange={handleFileChange}
                        />
                        <div className={`flex h-12 w-12 items-center justify-center rounded-full border-2 ${uploadEnabled ? "border-slate-400" : "border-slate-300"}`}>
                            <Plus className={`h-5 w-5 ${uploadEnabled ? "text-slate-400" : "text-slate-300"}`} />
                        </div>
                        <p className={`text-sm font-semibold ${uploadEnabled ? "text-slate-700" : "text-slate-400"}`}>
                            {!uploadEnabled
                                ? t("CONTENT_UPLOAD_SELECT_FILE_TYPE_FIRST")
                                : `${t("CONTENT_UPLOAD_UPLOAD")} ${selectedFileType.toLowerCase() || t("CONTENT_UPLOAD_YOUR")} ${t("CONTENT_UPLOAD_FILE")}`}
                        </p>
                        <p className="text-xs text-slate-400">
                            {!uploadEnabled ? t("CONTENT_UPLOAD_SELECT_FILE_TYPE_HELPER") : helperText}
                        </p>
                    </div>
                ) : null}

                {!canUpload && !hasPreview ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <p className="text-sm text-slate-400">{t("CONTENT_UPLOAD_NO_FILE_UPLOADED")}</p>
                    </div>
                ) : null}
            </div>
            {isUploading && (
                <div className="mt-2">
                    <div className="mb-1 flex items-center justify-between">
                        <p className="text-xs text-slate-500">{t("COMMON_UPLOADING")}</p>
                        <p className="text-xs font-medium text-primary">{uploadProgress}%</p>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-slate-200">
                        <div
                            className="h-1.5 rounded-full bg-primary transition-all duration-200"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default ContentUploadPanel;
